import { Box, Button, Stack, SxProps, Typography, useTheme } from '@mui/material';

import { DateTime } from '@/utility/luxon';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import MyTransactionsEmptyState from './my-transactions-empty-state';

import { PATHS } from '@/constants/routes';
import { TransactionRow } from './transaction-row';
import isEmpty from 'lodash/isEmpty';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useRouter } from 'next/router';
import { useTransactionsFiltersStore } from '../../store/transactions-filters-store';
import { MovieLoaderWrapper } from '@/components/shared/movie-loader-wrapper';
import ImageBoxFuture from '@/components/shared/image-box-future';
import { Transaction } from '@/features/wallet/services/types';

const NotfoundIcon = '/icons/shared/not-found-no-data.svg';

interface MyTransactionsProps {
  accordionSx: SxProps;
  transactions: Transaction[];
  isInfiniteScroll?: boolean;
  isFetching?: boolean;
  isErrorTransactions?: boolean;
  refetchTransactions: () => void;
}

interface TransactionsGroupItem {
  date: string;
  transactions: Transaction[];
}
export const MyTransactions: FC<MyTransactionsProps> = ({
  accordionSx,
  transactions,
  isInfiniteScroll,
  isFetching,
  isErrorTransactions,
  refetchTransactions,
}) => {
  const theme = useTheme();

  const transactionsFilters = useTransactionsFiltersStore(
    s => s.transactionsFilters,
    shallow
  );

  const { push } = useRouter();

  // this gives an object with dates as keys
  const transactionsGroups = transactions?.reduce((groups, item) => {
    const date = item.createdAt.split('T')[0];
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(item);
    return groups;
  }, {});

  // Edit: to add it in the array format instead
  const transactionsGroupsList: TransactionsGroupItem[] = Object.keys(
    transactionsGroups || {}
  ).map(date => {
    return {
      date,
      transactions: transactionsGroups[date],
    };
  });

  return (
    <Stack
      sx={{
        ...accordionSx,
        padding: '24px !important',
        paddingRight: '10px !important',
        maxHeight: isInfiniteScroll ? 'none' : '500px',
      }}
    >
      {!isInfiniteScroll && (
        <Typography
          sx={{
            fontSize: pxToRem(18),
            fontWeight: '700',
            marginBottom: '16px',
          }}
        >
          <FormattedMessage id='wallet.my.transactions' />
        </Typography>
      )}
      <Box
        sx={{
          overflowY: isInfiniteScroll ? 'none' : 'auto',
          paddingRight: '18px',
        }}
      >
        {isFetching ? (
          <Stack
            sx={{
              alignItems: 'center',
            }}
          >
            <MovieLoaderWrapper
              outerBox={{
                width: '200px',
              }}
            />
          </Stack>
        ) : isErrorTransactions ? (
          <Stack
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              marginY: '64px',
            }}
          >
            <ImageBoxFuture
              img={NotfoundIcon}
              sx={{
                width: pxToRem(288),
                height: pxToRem(178),
              }}
            />
            <Typography
              sx={{
                fontSize: pxToRem(20),
                fontWeight: '400',
                color: theme.palette.grey[50],
                marginTop: '34px',
              }}
            >
              <FormattedMessage id='movie_finder.sorry.something.went.wrong' />
            </Typography>
            <Button
              loading={isFetching}
              variant='contained'
              sx={{
                marginTop: '32px',
                width: '178px',
                height: '60px',
              }}
              onClick={() => refetchTransactions()}
            >
              <FormattedMessage id='global.try_again' />
            </Button>
          </Stack>
        ) : isEmpty(transactions) ? (
          <MyTransactionsEmptyState
            accordionSx={accordionSx}
            message={
              transactionsFilters.length > 0
                ? 'wallet.transactions.no.matching.results.for.selected.filters'
                : 'wallet.transactions.empty.state'
            }
          />
        ) : (
          transactionsGroupsList?.map?.((transactionGroupItem, index) => (
            <Box key={index}>
              <Typography
                sx={{
                  fontSize: pxToRem(16),
                  fontWeight: '400',
                  color: theme.palette.grey[50],
                  marginTop: index === 0 ? '' : '32px',
                }}
              >
                {DateTime.fromISO(transactionGroupItem.date).toFormat('DD')}
              </Typography>{' '}
              {transactionGroupItem.transactions.map((transactionItem, index) => (
                <TransactionRow
                  key={index}
                  type={transactionItem.type}
                  amount={transactionItem.amount}
                  expireDate={transactionItem.expireAt}
                />
              ))}
            </Box>
          ))
        )}
      </Box>

      {!isInfiniteScroll && !isErrorTransactions && !isEmpty(transactions) && (
        <Button
          variant='text'
          onClick={() => push(PATHS.USER_AREA_TRANSACTIONS_CASHBACK)}
          sx={{
            fontSize: pxToRem(18),
            textDecoration: 'underline',
            color: theme.palette.primary.main,
            marginRight: 'auto',
            marginTop: '20px',
          }}
        >
          <FormattedMessage id='wallet.view.all.transactions' />
        </Button>
      )}
    </Stack>
  );
};

export default MyTransactions;
